import { Button } from "@admin/components/atoms/Button";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { deleteBulkShift, Shift } from "@enos-monorepo/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface ClearThisDaysShiftsModalProps extends ModalProps {
  date: Date;
  shiftsWillBeDeleted?: Shift[];
}
export function ClearThisDaysShiftsModal({
  date,
  shiftsWillBeDeleted,
  ...props
}: ClearThisDaysShiftsModalProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: deleteBulkShift,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      props?.onClose?.();
      toast.success(t("shift.The day has been cleared"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      toast.error(t("shift.Unable to clear the day"), {
        duration: 4000,
      });
    },
  });

  const onSubmit = () => {
    const shiftIds = shiftsWillBeDeleted
      ?.map((shift) => shift.id)
      .filter((id): id is string => id !== undefined);
    mutation.mutate(shiftIds ?? []);
  };

  return (
    <Modal
      title={t("shift.Clear Shifts for Day", {
        day:
          date &&
          new Intl.DateTimeFormat(i18n.language, {
            month: "short", // 'MMM' karşılığı
            day: "2-digit", // 'dd' karşılığı
          }).format(date),
      })}
      {...props}
      actionButtons={
        <>
          <Button variant="secondary" type="button" onClick={props?.onClose}>
            {t("common.Discard")}
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            onClick={onSubmit}
          >
            {t("shift.Clear Shifts")}
          </Button>
        </>
      }
    >
      <form>
        <div>
          {t("shift.Are you sure you want to delete all shifts for day", {
            date:
              date &&
              Intl.DateTimeFormat(i18n.language, {
                month: "short", // 'MMM' karşılığı
                day: "2-digit", // 'dd' karşılığı
                weekday: "long", // 'EEEE' karşılığı
              }).format(date),
          })}
        </div>
      </form>
    </Modal>
  );
}
